/**
 * The one place an automation's on/off state becomes words (A4).
 *
 * A PILL, NOT A TOGGLE, and that is the whole point. A4 can read the control
 * plane and cannot write it: every write endpoint arrives with A5. A switch
 * that does not switch — even a disabled one — invites the click it cannot
 * honour, and a greyed-out control reads as "you may not", which is a different
 * and untrue statement. So there is no `onClick` here, no `disabled` input, and
 * nothing focusable.
 *
 * PC4: AN AUTOMATION IN PREPARATION IS ASKED FIRST, off `runtime_mode`. It has
 * no switch and nothing that starts it, so neither the capability rule below
 * ("Manuell") nor `enabled` may describe it. Keep that check in front.
 *
 * THE OTHER STATES COME OFF `capabilities.enable_disable`, NOT off `enabled`.
 * `requirement_reminder` reports `enabled: null` today, and deriving "manual"
 * from that null would break the moment a type arrives that CAN be switched and
 * happens to report null for a different reason. The capability is the question
 * being asked — "does this automation have an on/off at all" — so it is the
 * field that decides.
 *
 *   runtime_mode preparing    ->  In Vorbereitung (PC4: nothing runs yet)
 *   enable_disable === false  ->  Manuell   (no switch exists)
 *   enabled === true          ->  Aktiv
 *   enabled === false         ->  Aus
 *   anything else             ->  Unbekannt (fail-soft, never "Aktiv")
 *
 * THE FALLBACK NEVER CLAIMS ACTIVITY. An automation whose state we cannot read
 * must not be painted as running: the expensive mistake here is telling an
 * operator that birthday mail is going out when nobody knows whether it is.
 *
 * THIS IS THE A5 SEAM. The list row and the detail overview both render THIS
 * component, so the slice that adds the real toggle changes one file and
 * inherits both surfaces. No toggle-shaped props are introduced today — a dead
 * `onToggle` would be scaffolding for a design A5 has not made yet.
 */
import React from "react";

import { automationToneClass } from "./automationLabels";

const PILL_BASE =
  "inline-flex items-center rounded-md border px-2 py-0.5 text-[11.5px] font-semibold";

/**
 * `{ label, tone }` for an automation, or the fail-soft neutral state.
 *
 * Exported and pure so the status rule can be asserted directly, without
 * rendering three pills to ask one question — the same reasoning
 * `getSidebarSectionForLocation` records for itself.
 */
export function automationStatusPresentation(automation) {
  // PC4: an automation being prepared has no switch AND nothing that starts
  // it — no clock, no button — so "Manuell" would be as untrue as "Aktiv". The
  // runtime mode is the only field that says so, and it is asked first so no
  // later branch can paint a prepared automation as running.
  if (automation?.runtime_mode === "preparing") {
    return { label: "In Vorbereitung", tone: "muted" };
  }
  const canToggle = automation?.capabilities?.enable_disable;
  if (canToggle === false) return { label: "Manuell", tone: "muted" };
  if (automation?.enabled === true) return { label: "Aktiv", tone: "positive" };
  if (automation?.enabled === false) return { label: "Aus", tone: "neutral" };
  return { label: "Unbekannt", tone: "muted" };
}

/**
 * The same four states, said in a sentence (Redesign).
 *
 * THE LIST SAYS IT IN A WORD AND THE DETAIL SAYS IT IN A SENTENCE, because they
 * are answering the same question with different amounts of room. A pill in a
 * row of six has to be scannable; the detail's status card is the one place a
 * reader has stopped to look at a single automation, and there "Aus" alone
 * leaves the obvious follow-up — and then what? — unanswered.
 *
 * DERIVED FROM `automationStatusPresentation`, NOT re-branched. The rule that
 * decides which of the four states applies is subtle (it reads the CAPABILITY
 * first, then `enabled`, and never falls back to "Aktiv"), and a second copy of
 * those branches would be a second place to get it wrong. This function only
 * translates a decided label into more words, so the pill and the card cannot
 * disagree about what is true — only about how much they say.
 *
 * "ausgeschaltet" RATHER THAN "inaktiv" IS DELIBERATE: "aktiv" is a substring
 * of "inaktiv", so the two headlines would be indistinguishable to any check
 * that searches for one of them — including the tests that assert the screen
 * does NOT claim the opposite state.
 *
 * THE CARD CARRIES ITS OWN TONE, AND IT IS NOT THE PILL'S. The WORD is shared —
 * both renderings come from the one branch in `automationStatusPresentation`,
 * so they can never disagree about what is true. The COLOUR is not: a switched-
 * on automation shows a green pill in the list and a blue card in the detail,
 * and that is the design, not a drift. Green on this page means "this went
 * well" and belongs to run outcomes — the Erfolgreich count, a succeeded run.
 * Blue is the interactive Vantio surface: the selected row, the chosen tab, the
 * switch, and the card that holds it. A badge the size of a word can carry a
 * status colour cheaply; a full-width surface tinted green would make "active"
 * read as "successful", which is a different claim about a different thing.
 */
const STATUS_STATEMENTS = {
  Aktiv: {
    headline: "Automatisierung aktiv",
    description:
      "Diese Automatisierung ist eingeschaltet und wird automatisch ausgeführt.",
    iconKey: "active",
    tone: "primary",
  },
  Aus: {
    headline: "Automatisierung ausgeschaltet",
    description:
      "Diese Automatisierung ist ausgeschaltet und wird nicht ausgeführt.",
    iconKey: "off",
    tone: "neutral",
  },
  // Avoids "aktiv" and "automatisch" on purpose: the words of the states it
  // is not are exactly what a reader must not find here.
  "In Vorbereitung": {
    headline: "In Vorbereitung",
    description:
      "Die E-Mail-Vorlage kann bereits vorbereitet werden. Ausgeführt wird diese Automatisierung noch nicht.",
    iconKey: "preparing",
    tone: "muted",
  },
  Manuell: {
    headline: "Manueller Start",
    description:
      "Diese Automatisierung läuft nicht nach Plan und wird von Hand ausgelöst.",
    iconKey: "manual",
    tone: "muted",
  },
  Unbekannt: {
    headline: "Status unbekannt",
    description:
      "Der Zustand dieser Automatisierung konnte nicht gelesen werden.",
    iconKey: "unknown",
    tone: "muted",
  },
};

export function automationStatusStatement(automation) {
  const { label } = automationStatusPresentation(automation);
  return { ...STATUS_STATEMENTS[label], label };
}

export default function AutomationStatusPill({ automation, className = "" }) {
  const { label, tone } = automationStatusPresentation(automation);
  return (
    <span className={`${PILL_BASE} ${automationToneClass(tone)} ${className}`}>
      {label}
    </span>
  );
}
